import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('/');
  const [isScrolled, setIsScrolled] = useState(false);
  const [backdropBlur, setBackdropBlur] = useState(0);
  const [backdropOpacity, setBackdropOpacity] = useState(0);
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [submenuOpen, setSubmenuOpen] = useState<string | null>(null);
  const location = useLocation();
  const { scrollY } = useScroll();
  const navRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const contactDropdownRef = useRef<HTMLDivElement>(null);
  const prevScrollPosition = useRef(0);
  const ticking = useRef(false);
  
  // Helper function to calculate responsive sidebar width
  const getSidebarWidth = (width: number): string => {
    if (width >= 1400) return '550px';
    if (width >= 1200) return '500px';
    if (width >= 992) return '450px';
    if (width >= 768) return '420px';
    if (width >= 576) return '380px';
    if (width >= 480) return '90%';
    return '100%';
  };
  
  useMotionValueEvent(scrollY, "change", (latest) => {
    if (ticking.current) return;
    prevScrollPosition.current = latest;
    ticking.current = true;
    requestAnimationFrame(() => {
      const newIsScrolled = prevScrollPosition.current > 20;
      setIsScrolled(newIsScrolled);
      const newBlur = newIsScrolled ? Math.min(15, Math.max(8, 8 + (prevScrollPosition.current / 100))) : 0;
      setBackdropBlur(newBlur);
      const newOpacity = newIsScrolled ? 
        Math.min(0.4, Math.max(0.2, 0.2 + (prevScrollPosition.current / 500))) : 0;
      setBackdropOpacity(newOpacity);
      ticking.current = false;
    });
  });
  
  useEffect(() => {
    setIsOpen(false);
    setSubmenuOpen(null);
    setActiveSection(location.pathname);
  }, [location]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && 
          menuRef.current && 
          !menuRef.current.contains(e.target as Node) && 
          navRef.current && 
          !navRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSubmenuOpen(null);
      }

      if (contactDropdownOpen && 
          contactDropdownRef.current && 
          !contactDropdownRef.current.contains(e.target as Node)) {
        setContactDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, contactDropdownOpen]);
  useEffect(() => {
    if (isOpen) {
      // Hide all scrollbars when mobile nav is open - including main page
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      // Add class to hide scrollbars globally
      const style = document.createElement('style');
      style.id = 'hide-scrollbars-style';
      style.textContent = `
        body, html, #root {
          overflow: hidden !important;
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        body::-webkit-scrollbar, 
        html::-webkit-scrollbar,
        #root::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
      `;
      document.head.appendChild(style);
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      // Remove the scrollbar hiding style
      const style = document.getElementById('hide-scrollbars-style');
      if (style) {
        style.remove();
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      const style = document.getElementById('hide-scrollbars-style');
      if (style) {
        style.remove();
      }
    };
  }, [isOpen]);
  interface PathProps {
    d?: string;
    variants?: {
      closed?: { d?: string; opacity?: number };
      open?: { d?: string; opacity?: number };
    };
    transition?: {
      duration?: number;
    };
    initial?: string;
    animate?: string;
  }
  
  const Path: React.FC<PathProps> = (props) => (
    <motion.path
      fill="transparent"
      strokeWidth="3"
      stroke="white"
      strokeLinecap="round"
      {...props}
    />
  );

  const menuButtonVariants = {
    closed: { opacity: 1 },
    open: { opacity: 1, transition: { duration: 0.2 } }
  };
  const menuVariants = {
    closed: {
      opacity: 0,
      x: "100%",
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 40,
        when: "afterChildren" as const,
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    },
    open: {
      opacity: 1,
      x: "0%",
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 30,
        when: "beforeChildren" as const,
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const menuItemVariants = {
    closed: {
      opacity: 0,
      x: 20,
      transition: { duration: 0.2 }
    },
    open: { 
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: [0.25, 1, 0.5, 1] as [number, number, number, number] }
    }
  };

  interface SubMenuItem {
    path: string;
    label: string;
    description?: string;
  }

  interface NavItem {
    path: string;
    label: string;
    isAction?: boolean;
    isDropdown?: boolean;
    isExternal?: boolean;
    hasSubmenu?: boolean;
    submenu?: SubMenuItem[];
  }

  const navItems: NavItem[] = [
    { path: '/', label: 'Home' },
    { 
      path: '/services', 
      label: 'Services',
      hasSubmenu: true,
      submenu: [
        { path: '/services/residential', label: 'Residential', description: 'Home extensions & renovations' },
        { path: '/services/commercial', label: 'Commercial', description: 'Business construction projects' },
        { path: '/services/industrial', label: 'Industrial', description: 'Large-scale industrial builds' },
        { path: '/services/restoration', label: 'Restoration', description: 'Heritage & restoration work' },
        { path: '/services', label: 'All Services', description: 'View complete service list' }
      ]
    },
    { path: '/gallery', label: 'Gallery' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact', isAction: true, isDropdown: true }
  ];
  const desktopOnlyStyle = {
    display: 'none'
  };
  useEffect(() => {
    const applyMediaStyles = () => {
      // Update window width state for reactive sidebar sizing
      setWindowWidth(window.innerWidth);
      
      const desktopNav = document.querySelector('.desktop-nav');
      const mobileMenuButton = document.querySelector('.mobile-menu-button');
      const mobileMenu = document.querySelector('.mobile-menu');
      
      if (desktopNav) {
        if (window.innerWidth >= 992) {
          (desktopNav as HTMLElement).style.display = 'block';
        } else {
          (desktopNav as HTMLElement).style.display = 'none';
        }
      }
      
      if (mobileMenuButton) {
        if (window.innerWidth >= 992) {
          (mobileMenuButton as HTMLElement).style.display = 'none';
        } else {
          (mobileMenuButton as HTMLElement).style.display = 'flex';
        }
      }
      
      if (mobileMenu) {
        // Responsive sidebar width based on screen size
        (mobileMenu as HTMLElement).style.maxWidth = getSidebarWidth(window.innerWidth);
      }
    };
    
    applyMediaStyles();
    window.addEventListener('resize', applyMediaStyles);
    
    return () => {
      window.removeEventListener('resize', applyMediaStyles);
    };
  }, [isOpen]);

  return (
    <>
      {/* Main Navigation Bar - Mobile First Approach */}
      <motion.header
        className="nav-header"
        initial={{ y: -100 }}
        animate={{ 
          y: 0,
          boxShadow: isScrolled ? "0 2px 10px rgba(0, 0, 0, 0.1)" : "none"
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backdropFilter: `blur(${backdropBlur}px)`,
          background: `linear-gradient(to bottom, rgba(0, 0, 0, ${backdropOpacity + 0.1}), rgba(0, 0, 0, ${backdropOpacity}))`,
          padding: isScrolled ? "10px 0" : "15px 0",
          transition: "all 0.3s cubic-bezier(0.25, 1, 0.5, 1)",
          isolation: 'isolate',
        }}
      >
        <div 
          className="nav-container" 
          ref={navRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 5vw'
          }}
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Link to="/" className="nav-logo-link" style={{ display: 'block' }}>
              <motion.img 
                src="/img/logo-white.svg" 
                alt="NLC Group UK" 
                animate={{ 
                  height: isScrolled ? '38px' : '45px',
                  scale: isScrolled ? 0.95 : 1
                }}
                transition={{ 
                  duration: 0.4, 
                  ease: [0.25, 1, 0.5, 1] 
                }}
                style={{ 
                  height: '45px',
                  filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))'
                }}
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation - Hidden on Mobile */}
          <motion.nav 
            className="desktop-nav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              ...desktopOnlyStyle,
              isolation: 'isolate',
            }}
          >
            <motion.ul 
              style={{
                display: 'flex',
                listStyle: 'none',
                gap: '2vw',
                margin: 0,
                padding: 0
              }}
            >
              {navItems.map((item, index) => (
                <motion.li
                  key={index}
                  custom={index}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    delay: index * 0.1, 
                    duration: 0.4,
                    ease: [0.25, 1, 0.5, 1]
                  }}
                  style={{ position: 'relative' }}
                >
                  {item.isExternal ? (
                    <motion.a
                      href={item.path}
                      className={`nav-link ${item.isAction ? 'nav-action' : ''}`}
                      whileHover={{ 
                        scale: item.isAction ? 1.05 : 1.02,
                        textShadow: item.isAction ? 'none' : '0 0 8px rgba(255,255,255,0.7)'
                      }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        textDecoration: 'none',
                        padding: item.isAction ? '8px 20px' : '8px 12px',
                        fontSize: '16px',
                        fontWeight: 500,
                        background: item.isAction ? 'var(--brand-color)' : 'transparent',
                        borderRadius: item.isAction ? '30px' : '0',
                        boxShadow: item.isAction ? '0 4px 10px rgba(0, 0, 0, 0.1)' : 'none',
                        display: 'block',
                        position: 'relative',
                        overflow: 'hidden',
                        color: item.isAction ? 'white' : 'white',
                        mixBlendMode: item.isAction ? 'normal' : 'difference',
                        transition: 'color 0.3s ease, text-shadow 0.3s ease',
                      }}
                    >
                      {item.label}
                      {item.isAction && (
                        <motion.div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            transform: 'translateX(-100%)'
                          }}
                          animate={{
                            transform: ['translateX(-100%)', 'translateX(100%)']
                          }}
                          transition={{
                            repeat: Infinity,
                            repeatType: 'loop',
                            duration: 2,
                            ease: 'linear',
                            delay: 1
                          }}
                        />
                      )}
                    </motion.a>
                  ) : item.isDropdown ? (
                    <div style={{ position: 'relative' }} ref={contactDropdownRef}>
                      <div 
                        onClick={(e) => {
                          e.preventDefault();
                          setContactDropdownOpen(!contactDropdownOpen);
                        }}
                        style={{ position: 'relative' }}
                      >
                        <motion.button
                          whileHover={{ 
                            scale: 1.02,
                            backgroundColor: "rgba(17, 85, 161, 0.8)",
                          }}
                          whileTap={{ scale: 0.98 }}
                          style={{
                            padding: '8px 16px',
                            fontSize: '16px',
                            fontWeight: 600,
                            position: 'relative',
                            color: 'white',
                            border: 'none',
                            borderRadius: '24px',
                            backgroundColor: 'var(--brand-color)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                            overflow: 'hidden',
                            outline: 'none',
                          }}
                        >
                          <span style={{ color: 'white' }}>{item.label}</span>
                          <svg 
                            width="12" 
                            height="12" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="white" 
                            strokeWidth="3" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            style={{
                              transform: contactDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease'
                            }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </motion.button>
                        
                        {/* Contact Dropdown Menu */}
                        <AnimatePresence>
                          {contactDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: 10, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: 10, scale: 0.95 }}
                              transition={{ 
                                duration: 0.2,
                                ease: [0.25, 1, 0.5, 1]
                              }}
                              style={{
                                position: 'absolute',
                                top: 'calc(100% + 10px)',
                                right: 0,
                                background: 'white',
                                borderRadius: '12px',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                                padding: '8px',
                                width: '260px',
                                zIndex: 1000,
                                overflow: 'hidden'
                              }}
                            >
                              {/* Dropdown Header with Brand */}
                              <div
                                style={{
                                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                                  padding: '10px 15px', 
                                  marginBottom: '5px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <span style={{ 
                                  fontWeight: 600, 
                                  fontSize: '14px', 
                                  color: '#333'
                                }}>
                                  GET IN TOUCH
                                </span>
                                <div style={{
                                  background: 'var(--brand-color)',
                                  color: 'white',
                                  fontSize: '10px',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontWeight: 600
                                }}>
                                  10+ YEARS
                                </div>
                              </div>
                              
                              {/* Phone Option */}
                              <motion.a
                                href="tel:07725795516"
                                whileHover={{ 
                                  backgroundColor: 'rgba(0,0,0,0.03)',
                                  x: 3
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '12px 15px',
                                  color: '#333',
                                  textDecoration: 'none',
                                  borderRadius: '8px',
                                  margin: '3px 0'
                                }}
                                onClick={() => setContactDropdownOpen(false)}
                              >
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  background: 'rgba(17, 85, 161, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                  </svg>
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '14px' }}>Call Us</div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>07725 795516</div>
                                </div>
                              </motion.a>
                              
                              {/* Email Option */}
                              <motion.a
                                href="mailto:contact@nlcgroupuk.com"
                                whileHover={{ 
                                  backgroundColor: 'rgba(0,0,0,0.03)',
                                  x: 3
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '12px 15px',
                                  color: '#333',
                                  textDecoration: 'none',
                                  borderRadius: '8px',
                                  margin: '3px 0'
                                }}
                                onClick={() => setContactDropdownOpen(false)}
                              >
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  background: 'rgba(234, 67, 53, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EA4335" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                    <polyline points="22,6 12,13 2,6"></polyline>
                                  </svg>
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '14px' }}>Email Us</div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>contact@nlcgroupuk.com</div>
                                </div>
                              </motion.a>
                              
                              {/* Message Us Option */}
                              <motion.a
                                href="/contact"
                                whileHover={{ 
                                  backgroundColor: 'rgba(0,0,0,0.03)',
                                  x: 3
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '12px 15px',
                                  color: '#333',
                                  textDecoration: 'none',
                                  borderRadius: '8px',
                                  margin: '3px 0'
                                }}
                                onClick={() => {
                                  setContactDropdownOpen(false)
                                  navigate('/contact')
                                }}
                              >
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  background: 'rgba(79, 134, 198, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4F86C6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                    <line x1="8" y1="21" x2="16" y2="21"></line>
                                    <line x1="12" y1="17" x2="12" y2="21"></line>
                                  </svg>
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '14px' }}>Message Us</div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>Contact Form</div>
                                </div>
                              </motion.a>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  ) : (
                    <Link 
                      to={item.path} 
                      style={{ textDecoration: 'none' }}
                    >
                      <motion.div
                        whileHover={{ 
                          scale: 1.02,
                          textShadow: '0 0 8px rgba(255,255,255,0.7)'
                        }}
                        whileTap={{ scale: 0.98 }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '16px',
                          fontWeight: activeSection === item.path ? 600 : 500,
                          position: 'relative',
                          color: 'white',
                          transition: 'color 0.3s ease, text-shadow 0.3s ease',
                        }}
                      >
                        {item.label}
                        {activeSection === item.path && (
                          <motion.div
                            layoutId="activeIndicator"
                            style={{
                              position: 'absolute',
                              bottom: '0',
                              left: '0',
                              right: '0',
                              height: '2px',
                              background: 'var(--brand-color)',
                              borderRadius: '1px'
                            }}
                          />
                        )}
                      </motion.div>
                    </Link>
                  )}
                </motion.li>
              ))}
            </motion.ul>
          </motion.nav>

          {/* Mobile Menu Button */}
          <motion.button 
            className="mobile-menu-button"
            onClick={() => {
              setIsOpen(!isOpen);
              if (!isOpen) setSubmenuOpen(null);
            }}
            variants={menuButtonVariants}
            initial="closed"
            animate={isOpen ? "open" : "closed"}
            whileTap={{ scale: 0.9 }}
            whileHover={{ scale: 1.05 }}
            style={{
              outline: 'none',
              border: 'none',
              backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              backdropFilter: isScrolled ? 'blur(5px)' : 'none',
              cursor: 'pointer',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1001,
              borderRadius: '50%',
              boxShadow: isScrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
              transition: 'all 0.3s ease',
              WebkitTapHighlightColor: 'transparent', // Remove tap highlight on mobile
            }}
            aria-label="Menu"
          >
            <svg width="24" height="24" viewBox="0 0 23 23" style={{fill: 'white'}}>
              <Path
                variants={{
                  closed: { d: "M 2 2.5 L 20 2.5" },
                  open: { d: "M 3 16.5 L 17 2.5" }
                }}
                transition={{ duration: 0.3 }}
              />
              <Path
                d="M 2 9.423 L 20 9.423"
                variants={{
                  closed: { opacity: 1 },
                  open: { opacity: 0 }
                }}
                transition={{ duration: 0.3 }}
              />
              <Path
                variants={{
                  closed: { d: "M 2 16.346 L 20 16.346" },
                  open: { d: "M 3 2.5 L 17 16.346" }
                }}
                transition={{ duration: 0.3 }}
              />
            </svg>
          </motion.button>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mobile-menu"
            ref={menuRef}
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              maxWidth: getSidebarWidth(windowWidth),
              backgroundColor: '#fff',
              zIndex: 1000,
              boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              padding: '80px 8vw 40px',
              overflowY: 'auto',
              transition: 'max-width 0.3s ease-out'
            }}
          >
            {/* Close Button in top right */}
            <motion.button
              onClick={() => {
                setIsOpen(false);
                setSubmenuOpen(null);
              }}
              className="mobile-menu-close"
              variants={menuItemVariants}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(0,0,0,0.05)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 1002
              }}
              aria-label="Close menu"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </motion.button>

            {/* Brand Badge - Centered */}
            <motion.div
              variants={menuItemVariants}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '40px',
                textAlign: 'center'
              }}
            >
              <motion.img 
                src="/img/logo.svg" 
                alt="NLC Group UK" 
                style={{ 
                  width: '150px',
                  marginBottom: '15px' 
                }}
              />
              <motion.div
                style={{
                  backgroundColor: 'var(--brand-color)',
                  color: 'white',
                  fontSize: '12px',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  marginTop: '8px'
                }}
                whileHover={{ scale: 1.05 }}
              >
                <span style={{ marginRight: '4px' }}>10+ YEARS</span>
                <span style={{ fontSize: '10px', opacity: 0.9 }}>OF EXCELLENCE</span>
              </motion.div>
            </motion.div>

            {/* Mobile Menu Navigation */}
            <motion.nav
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                position: 'relative'
              }}
            >
              {/* Main Menu Grid */}
              <AnimatePresence mode="wait">
                {!submenuOpen && (
              <motion.ul
                    key="main-menu"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '12px'
                }}
              >
                {navItems.filter(item => !item.isAction).map((item, index) => (
                  <motion.li
                    key={index}
                    variants={menuItemVariants}
                    style={{
                          position: 'relative',
                          borderRadius: '12px',
                          padding: '16px',
                          border: '1px solid rgba(17,85,161,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          backdropFilter: 'blur(10px)',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          if (item.hasSubmenu) {
                            setSubmenuOpen(item.path);
                          }
                        }}
                      >
                        {/* Animated Mesh Blur Background */}
                        <motion.div
                          animate={{
                            backgroundPosition: [
                              '0% 0%',
                              '100% 100%',
                              '0% 100%',
                              '100% 0%',
                              '0% 0%'
                            ]
                          }}
                          transition={{
                            duration: 15 + index * 2,
                            repeat: Infinity,
                            ease: 'linear'
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `
                              radial-gradient(circle at ${20 + index * 10}% ${30 + index * 15}%, rgba(17,85,161,0.15) 0%, transparent 50%),
                              radial-gradient(circle at ${70 + index * 5}% ${60 + index * 10}%, rgba(39,125,218,0.12) 0%, transparent 50%),
                              radial-gradient(circle at ${40 + index * 8}% ${80 + index * 5}%, rgba(17,85,161,0.08) 0%, transparent 50%),
                              linear-gradient(135deg, rgba(17,85,161,0.05) 0%, rgba(39,125,218,0.05) 100%)
                            `,
                            backgroundSize: '200% 200%',
                            filter: 'blur(20px)',
                            opacity: 0.8,
                            zIndex: 0
                          }}
                        />
                        {/* Shimmer Effect */}
                        <motion.div
                          animate={{
                            x: ['-100%', '200%']
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            repeatDelay: 5 + index,
                            ease: 'easeInOut'
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '50%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                            zIndex: 1,
                            pointerEvents: 'none'
                          }}
                        />
                        {/* Color Difference Overlay */}
                        <motion.div
                          animate={{
                            opacity: [0.7, 0.85, 0.7]
                          }}
                          transition={{
                            duration: 4 + index * 0.5,
                            repeat: Infinity,
                            ease: 'easeInOut'
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: activeSection === item.path 
                              ? 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(240,248,255,0.9) 100%)'
                              : 'linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(248,252,255,0.8) 100%)',
                            backdropFilter: 'blur(2px)',
                            mixBlendMode: 'color-dodge',
                            zIndex: 1.5,
                            pointerEvents: 'none'
                          }}
                        />
                        <div style={{ position: 'relative', zIndex: 3, width: '100%' }}>
                      
                        {item.hasSubmenu ? (
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              color: activeSection === item.path ? 'var(--brand-color)' : '#333',
                              fontSize: '16px',
                              fontWeight: activeSection === item.path ? 600 : 500,
                              width: '100%',
                              textAlign: 'center'
                    }}
                  >
                            <span>{item.label}</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                          </motion.div>
                        ) : item.isExternal ? (
                      <motion.a
                        href={item.path}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                              justifyContent: 'center',
                          color: activeSection === item.path ? 'var(--brand-color)' : '#333',
                          textDecoration: 'none',
                              fontSize: '16px',
                          fontWeight: activeSection === item.path ? 600 : 500,
                              width: '100%',
                              textAlign: 'center'
                        }}
                      >
                        {item.label}
                      </motion.a>
                    ) : (
                      <Link 
                        to={item.path} 
                            style={{ textDecoration: 'none', width: '100%' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsOpen(false);
                            }}
                      >
                        <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                                justifyContent: 'center',
                            color: activeSection === item.path ? 'var(--brand-color)' : '#333',
                                fontSize: '16px',
                            fontWeight: activeSection === item.path ? 600 : 500,
                                textAlign: 'center'
                          }}
                        >
                          {item.label}
                        </motion.div>
                      </Link>
                    )}
                        </div>
                  </motion.li>
                ))}
              </motion.ul>
                )}

                {/* Submenu View */}
                {submenuOpen && (
                  <motion.div
                    key="submenu"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    {/* Back Button */}
                    <motion.button
                      onClick={() => setSubmenuOpen(null)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '14px 18px',
                        background: 'transparent',
                        border: '1px solid rgba(17,85,161,0.2)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'var(--brand-color)',
                        overflow: 'hidden',
                        backdropFilter: 'blur(10px)',
                        marginBottom: '8px'
                      }}
                    >
                      {/* Background Effects */}
                      <motion.div
                        animate={{
                          backgroundPosition: ['0% 0%', '100% 100%', '0% 0%']
                        }}
                        transition={{
                          duration: 10,
                          repeat: Infinity,
                          ease: 'linear'
                        }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `
                            radial-gradient(circle at 20% 30%, rgba(17,85,161,0.08) 0%, transparent 50%),
                            radial-gradient(circle at 80% 70%, rgba(39,125,218,0.06) 0%, transparent 50%),
                            linear-gradient(135deg, rgba(17,85,161,0.03) 0%, rgba(39,125,218,0.03) 100%)
                          `,
                          backgroundSize: '200% 200%',
                          filter: 'blur(20px)',
                          opacity: 0.8,
                          zIndex: 0
                        }}
                      />
                      <motion.div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(248,252,255,0.7) 100%)',
                          backdropFilter: 'blur(2px)',
                          mixBlendMode: 'color-dodge',
                          zIndex: 1,
                          pointerEvents: 'none'
                        }}
                      />
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 2 }}>
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                      <span style={{ position: 'relative', zIndex: 2 }}>Back</span>
                    </motion.button>

                    {/* Submenu Title */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: 'var(--brand-color)',
                        marginBottom: '4px',
                        paddingLeft: '4px'
                      }}
                    >
                      {navItems.find(item => item.path === submenuOpen)?.label}
                    </motion.div>

                    {/* Submenu Items */}
                    {navItems.find(item => item.path === submenuOpen)?.submenu?.map((subItem, index) => (
                      <Link
                        key={index}
                        to={subItem.path}
                        style={{ textDecoration: 'none' }}
                        onClick={() => {
                          setIsOpen(false);
                          setSubmenuOpen(null);
                        }}
                      >
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + index * 0.05 }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          style={{
                            position: 'relative',
                            padding: '16px 18px',
                            borderRadius: '12px',
                            border: '1px solid rgba(17,85,161,0.15)',
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)'
                          }}
                        >
                          {/* Animated Mesh Blur Background */}
                          <motion.div
                            animate={{
                              backgroundPosition: [
                                '0% 0%',
                                '100% 100%',
                                '0% 100%',
                                '100% 0%',
                                '0% 0%'
                              ]
                            }}
                            transition={{
                              duration: 12 + index * 2,
                              repeat: Infinity,
                              ease: 'linear'
                            }}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: `
                                radial-gradient(circle at ${15 + index * 12}% ${25 + index * 18}%, rgba(17,85,161,0.12) 0%, transparent 50%),
                                radial-gradient(circle at ${75 + index * 8}% ${65 + index * 12}%, rgba(39,125,218,0.10) 0%, transparent 50%),
                                radial-gradient(circle at ${45 + index * 10}% ${85 + index * 7}%, rgba(17,85,161,0.06) 0%, transparent 50%),
                                linear-gradient(135deg, rgba(17,85,161,0.04) 0%, rgba(39,125,218,0.04) 100%)
                              `,
                              backgroundSize: '200% 200%',
                              filter: 'blur(20px)',
                              opacity: 0.8,
                              zIndex: 0
                            }}
                          />
                          {/* Shimmer Effect */}
                          <motion.div
                            animate={{
                              x: ['-100%', '200%']
                            }}
                            transition={{
                              duration: 2.5,
                              repeat: Infinity,
                              repeatDelay: 4 + index * 0.5,
                              ease: 'easeInOut'
                            }}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '50%',
                              height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                              zIndex: 1,
                              pointerEvents: 'none'
                            }}
                          />
                          {/* Color Difference Overlay */}
                          <motion.div
                            animate={{
                              opacity: [0.65, 0.8, 0.65]
                            }}
                            transition={{
                              duration: 3.5 + index * 0.4,
                              repeat: Infinity,
                              ease: 'easeInOut'
                            }}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: activeSection === subItem.path
                                ? 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(240,248,255,0.9) 100%)'
                                : 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(248,252,255,0.75) 100%)',
                              backdropFilter: 'blur(2px)',
                              mixBlendMode: 'color-dodge',
                              zIndex: 1.5,
                              pointerEvents: 'none'
                            }}
                          />
                          <div style={{ position: 'relative', zIndex: 3 }}>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: 600,
                              color: activeSection === subItem.path ? 'var(--brand-color)' : '#333',
                              marginBottom: subItem.description ? '4px' : 0
                            }}>
                              {subItem.label}
                            </div>
                            {subItem.description && (
                              <div style={{
                                fontSize: '13px',
                                color: '#666',
                                fontWeight: 400
                              }}>
                                {subItem.description}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Contact Action Button */}
              <motion.div
                variants={menuItemVariants}
                style={{
                  marginTop: 'auto',
                  paddingTop: '30px'
                }}
              >
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px'
                }}>
                  <div style={{
                    marginBottom: '10px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"></path>
                      <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                    </svg>
                    CONTACT US
                  </div>
                  
                  {/* Phone Option */}
                  <motion.a
                    href="tel:07725795516"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(17, 85, 161, 0.07)',
                      color: '#333',
                      textDecoration: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}
                  >
                    <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--brand-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '16px' }}>Call Us</div>
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>07725 795516</div>
                    </div>
                  </motion.a>
                  
                  {/* Email Option */}
                  <motion.a
                    href="mailto:contact@nlcgroupuk.com"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(234, 67, 53, 0.07)',
                      color: '#333',
                      textDecoration: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}
                  >
                    <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '50%',
                      backgroundColor: '#EA4335',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '16px' }}>Email Us</div>
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>contact@nlcgroupuk.com</div>
                    </div>
                  </motion.a>
                  
                  {/* Message Us Option */}
                  <motion.a
                    href="/contact"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(79, 134, 198, 0.07)',
                      color: '#333',
                      textDecoration: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}
                    onClick={() => {
                      setContactDropdownOpen(false)
                      navigate('/contact')
                    }}
                  >
                    <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '50%',
                      backgroundColor: '#4F86C6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '16px' }}>Message Us</div>
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>Contact Form</div>
                    </div>
                  </motion.a>
                </div>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation; 